import {
    IQuickToolDialogManager, EFontFamilyApplyPart, EFontFamilyApplySlide,
    IQuickToolDialogManagerInjectable, IQuickToolDialogManagerDelegates, IQuickToolFontList, FontFamilyInfo,
} from './quick-tool-dialog-manager.interface';
import React from 'react';
import ReactDOM from 'react-dom';
import UniformFontDialog, { applySlidesFont } from './uniform-font';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
// import ReplaceFontDialog from './replace-font';
import PictureSizeDialog from './muti-pic-size';
import PicturePosDialog from './muti-pic-pos';
import ExportPictureDialog from './muti-export-image';
import TextDesensitizationDialog from './text-desensitization';
import { getVipInfo, isVip } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import { showModelToPay } from '@src/js/common/ark-extensions/dc-components/modal-to-pay';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType, TDWVer3Type, TDWVer9Type } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ToolbarType } from '@src/js/constant';
import { ListItem } from '@tencent/workbench';
import { getFontFamilyConfigData, getAllLegalFontFamilyConfigData } from '@editor/src/js/constants/toolbar-data';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ApiError, ErrorCode } from '@src/framework/docs-api/error-code';

export const QUICK_TOOL_AID_FEATURE = 'pptquicktool';

export enum QUICK_TOOL_AID_POSITION {
    RIGHT_MENU = 'rightmenu',
    TOOLBAR = 'toolbar',
    AI = 'aisidebar',
}

export interface DialogProps {
    visible: boolean;
    onClose: () => void;
    onCancel: () => void;
    onOk: (apprec: string, name: string) => boolean;
    position: QUICK_TOOL_AID_POSITION,
    FontListModule: React.ReactNode;
    QuickToolFontList: IQuickToolFontList;
    services: IQuickToolDialogManagerDelegates;
    fontFamilyInfo?: FontFamilyInfo;
}


export class QuickToolDialogManager implements IQuickToolDialogManager {
    private dialogContainer = document.createElement('div');
    private dialogType: string;
    private position: QUICK_TOOL_AID_POSITION;
    private FontListModule: React.ReactNode;
    private services: IQuickToolDialogManagerDelegates;

    public constructor(private injectable: IQuickToolDialogManagerInjectable) {
    }

    public setServiceDelegates(services: IQuickToolDialogManagerDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    /**
     * @param dialogType 快捷美化弹窗类型,统一字体/批量图片处理/etc.
     * @param position 点击的位置，上报用，默认工具栏
     * @param onSelect ai相关接口
     * @param onCancel ai相关接口
     */
    public async showDialog(
        dialogType: string,
        position: QUICK_TOOL_AID_POSITION = QUICK_TOOL_AID_POSITION.TOOLBAR,
        onSelect?: () => void,
        onCancel?: () => void,
    ) {
        this.FontListModule = await this.injectable?.getFontListFontListModule();
        this.position = position;
        this.dialogType = dialogType;
        this.updateDialog(true, position, onSelect, onCancel);
        tdw({
            opername: TDWOperationType.DOC_VIP_SLIDEQUICKTOOL,
            module: TDWModuleType.FUNCTION,
            action: TDWActionType.MENU_BAR_CLICK,
            eventcode: `${TDWOperationType.DOC_VIP_SLIDEQUICKTOOL}-${TDWModuleType.PC_TOOLBAR}-${TDWActionType.MENU_BAR_CLICK}`,
            ver5: getVipInfo(),
            ver6: this.getVer6(),
            ver9: TDWVer9Type.TOOLBAR,
        });
    }

    /**
     * 展示统一字体面板，由于一开始设计时几个面板参数相同，但后续统一字体对外扩展了一些能力导致添加了额外的参数，单独给它封个方法
     * @param position 点击的位置，上报用，默认工具栏
     * @param onSelect ai相关接口
     * @param onCancel ai相关接口
     * @param fontFamilyInfo 统一字体面板的默认选中字体
     */
    public async showUniformFontDialog(
        position: QUICK_TOOL_AID_POSITION = QUICK_TOOL_AID_POSITION.TOOLBAR,
        onSelect?: () => void,
        onCancel?: () => void,
        fontFamilyInfo?: FontFamilyInfo,
    ) {
        this.FontListModule = await this.injectable?.getFontListFontListModule();
        this.position = position;
        this.dialogType = ActionType.UNIFORM_FONT;
        this.updateDialog(true, position, onSelect, onCancel, fontFamilyInfo);
        tdw({
            opername: TDWOperationType.DOC_VIP_SLIDEQUICKTOOL,
            module: TDWModuleType.FUNCTION,
            action: TDWActionType.MENU_BAR_CLICK,
            eventcode: `${TDWOperationType.DOC_VIP_SLIDEQUICKTOOL}-${TDWModuleType.PC_TOOLBAR}-${TDWActionType.MENU_BAR_CLICK}`,
            ver5: getVipInfo(),
            ver6: this.getVer6(),
            ver9: TDWVer9Type.TOOLBAR,
        });
    }
    public hideDialog = () => {
        ReactDOM.unmountComponentAtNode(this.dialogContainer);
    };

    public handleClickOk = (apprec: string, name: string): boolean => {
        const action = this.getAction(true);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TOOLBAR,
            action,
            eventcode: `${TDWOperationType.DOC_PPT}-${TDWModuleType.PC_TOOLBAR}-${action}`,
            ver3: this.getVer3(),
            ver5: getVipInfo(),
        });
        if (isVip()) {
            return true;
        }
        showModelToPay({
            position: this.position,
            feature: QUICK_TOOL_AID_FEATURE,
            apprec,
            name,
        });
        return false;
    };

    public async updateDialog(
        visible: boolean,
        position: QUICK_TOOL_AID_POSITION,
        onSelect?: () => void,
        onCancel?: () => void,
        fontFamilyInfo?: FontFamilyInfo,
    ) {
        const Dialog = this.getDialogByType();
        const dialogOnOk = (apprec: string, name: string): boolean => {
            const okSuccess = this.handleClickOk(apprec, name);
            if (okSuccess) {
                // 点击确定后成功才触发ok回调，否则则是可能弹出开通vip弹框，不做响应
                onSelect?.();
            }
            return okSuccess;
        };
        const dialogOnCancel = (): void => {
            onCancel?.();
        };
        ReactDOM.render(<Dialog
            visible={visible}
            onClose={this.hideDialog}
            onOk={dialogOnOk}
            onCancel={dialogOnCancel}
            position={position}
            FontListModule={this.FontListModule}
            QuickToolFontList={this.injectable?.getQuickToolFontList()}
            services={this.services}
            fontFamilyInfo={fontFamilyInfo}
        />, this.dialogContainer);
    }

    /**
     * 字体切换功能 - 获取推荐字体（云字体本期暂不支持）
     * @param batchIndex 批次号
     * @param oneBatchCount 一个批次包含的字体数量
     */
    public getRecommendFontList(batchIndex = 0, oneBatchCount = 5): string[] {
        const fontFamilyList: ListItem[] = getFontFamilyConfigData();
        // 容纳最大的批次
        const maxBatch = Math.ceil(fontFamilyList.length / oneBatchCount);
        // 超过最大的批次数则从头开始
        const finalBatchIndex = batchIndex > (maxBatch - 1) ? batchIndex % maxBatch : batchIndex;
        return fontFamilyList
            .slice(finalBatchIndex * oneBatchCount, (finalBatchIndex * oneBatchCount) + oneBatchCount)
            .map(e => e.content as string);
    }

    /**
     * 字体切换功能 - 应用字体
     * @param fontName 要切换的字体
     * @param applyPart 页内应用字体的范围（标题+正文/标题/正文）
     * @param applySlides 应用的页(全部页/当前页)
     */
    public async applyFont(
        fontName: string,
        applyPart = EFontFamilyApplyPart.ALL,
        applySlides = EFontFamilyApplySlide.ALL,
    ): Promise<void> {
        if (!fontName) {
            throw new ApiError(ErrorCode.InvalidFont);
        }
        const targetFont = getAllLegalFontFamilyConfigData()
            .find(e => (e.content as any).toLowerCase() === fontName.toLowerCase());
        if (!targetFont) {
            throw new ApiError(ErrorCode.InvalidFont);
        }
        // 涉及的slide
        const { selectionService } = this.services;
        const needApplySlides = (applySlides === EFontFamilyApplySlide.CURRENT
            ? [selectionService.currentSlide] : selectionService.slides) || [];
        if (!needApplySlides.length) {
            throw new ApiError(ErrorCode.SlideNotExist);
        }
        // 目标字体
        const targetTitle = applyPart !== EFontFamilyApplyPart.CONTENT ? targetFont.id : undefined;
        const targetContent = applyPart !== EFontFamilyApplyPart.TITLE ? targetFont.id : undefined;
        const fontFamilyInfo = {
            titleFontEa: targetTitle, titleFontLatin: targetTitle,
            contentFontEa: targetContent, contentFontLatin: targetContent,
        };
        try {
            await applySlidesFont(needApplySlides as ISlideModel[], fontFamilyInfo);
        } catch (err) {
            window.log.error(`applyFont -error: ${err}`);
            throw err;
        }
    }

    private getDialogByType() {
        const { dialogType } = this;
        if (dialogType === ActionType.UNIFORM_FONT) {
            return UniformFontDialog;
        }
        // if (dialogType === ActionType.REPLACE_FONT) {
        //     return ReplaceFontDialog;
        // }
        if (dialogType === ActionType.MUTI_PICTURE_SIZE_SETTING) {
            return PictureSizeDialog;
        }
        if (dialogType === ActionType.MUTI_PICTURE_POS_SETTING) {
            return PicturePosDialog;
        }
        if (dialogType === ActionType.MUTI_PICTURE_EXPORT) {
            return ExportPictureDialog;
        }
        if (dialogType === ActionType.DIGITAL_CONTENT_DESENSITIZATION) {
            return TextDesensitizationDialog;
        }
        return UniformFontDialog;
    }

    private getVer6() {
        const { dialogType } = this;
        if (dialogType === ActionType.UNIFORM_FONT) {
            return 'unifyfont';
        }
        if (dialogType === ActionType.MUTI_PICTURE_SIZE_SETTING) {
            return 'batsetpicsize';
        }
        if (dialogType === ActionType.MUTI_PICTURE_POS_SETTING) {
            return 'batsetpiclay';
        }
        if (dialogType === ActionType.MUTI_PICTURE_EXPORT) {
            return 'batdownloadpic';
        }
        return '';
    }

    // eslint-disable-next-line complexity
    private getAction(fromOk = false): string {
        const { dialogType } = this;
        if (dialogType === ActionType.UNIFORM_FONT) {
            if (fromOk) {
                return TDWActionType.SHORTCUT_TOOL_UNIFORM_FONT_OK;
            }
            return TDWActionType.SHORTCUT_TOOL_UNIFORM_FONT_CLICK;
        }
        // if (dialogType === ActionType.REPLACE_FONT) {
        //     if (fromOk) {
        //         return TDWActionType.SHORTCUT_TOOL_REPLACE_FONT_OK;
        //     }
        //     return TDWActionType.SHORTCUT_TOOL_REPLACE_FONT_CLICK;
        // }
        if (dialogType === ActionType.MUTI_PICTURE_SIZE_SETTING) {
            if (fromOk) {
                return TDWActionType.SHORTCUT_TOOL_IMAGE_SIZE_OK;
            }
            return TDWActionType.SHORTCUT_TOOL_IMAGE_SIZE_CLICK;
        }
        if (dialogType === ActionType.MUTI_PICTURE_POS_SETTING) {
            if (fromOk) {
                return TDWActionType.SHORTCUT_TOOL_IMAGE_LAYOUT_OK;
            }
            return TDWActionType.SHORTCUT_TOOL_IMAGE_LAYOUT_CLICK;
        }
        if (dialogType === ActionType.MUTI_PICTURE_EXPORT) {
            if (fromOk) {
                return TDWActionType.SHORTCUT_TOOL_IMAGE_DOWNLOAD_OK;
            }
            return TDWActionType.SHORTCUT_TOOL_IMAGE_DOWNLOAD_CLICK;
        }
        return TDWActionType.SHORTCUT;
    }

    private getVer3() {
        if (this.position === QUICK_TOOL_AID_POSITION.TOOLBAR) {
            return this.services.toolbarStateManager.getToolbarMode() === ToolbarType.SINGLE_TOOLBAR
                ? TDWVer3Type.SINGLE_TOOLBAR
                : TDWVer3Type.DOUBLE_TOOLBAR;
        }
        return TDWVer3Type.RIGHT_MENU;
    }
}
